import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { AppPage } from '../types';
import { resolvePageFromPath } from '../utils/navigation';
import { addPageVisit } from '../utils/historyTracking';

// Extract subpage from URL search params based on page type
const getSubpageFromUrl = (page: AppPage, search: string): string | undefined => {
  const params = new URLSearchParams(search);
  
  if (page === 'personal-growth' || page === 'journal' || page === 'ehr') {
    const tab = params.get('tab');
    return tab || undefined;
  }
  
  if (page === 'health-programs' || page === 'cbt-programs') {
    const program = params.get('program');
    return program || undefined;
  }
  
  return undefined;
};

export const usePageTracking = () => {
  const location = useLocation();
  const startTimeRef = useRef<number>(Date.now());
  const currentPageRef = useRef<AppPage | null>(null);
  const currentSubpageRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const page = resolvePageFromPath(location.pathname);
    const subpage = getSubpageFromUrl(page, location.search);
    
    // Check if page or subpage changed
    const pageChanged = currentPageRef.current !== page;
    const subpageChanged = currentSubpageRef.current !== subpage;
    
    // Track when leaving previous page/subpage
    if (currentPageRef.current && (pageChanged || subpageChanged)) {
      const duration = Date.now() - startTimeRef.current;
      if (duration > 1000) { // Only track if stayed on page/subpage for more than 1 second
        addPageVisit(currentPageRef.current, duration, currentSubpageRef.current);
      }
    }

    // Start tracking new page/subpage
    currentPageRef.current = page;
    currentSubpageRef.current = subpage;
    startTimeRef.current = Date.now();

    // Track when component unmounts or page changes
    return () => {
      if (currentPageRef.current) {
        const duration = Date.now() - startTimeRef.current;
        if (duration > 1000) {
          addPageVisit(currentPageRef.current, duration, currentSubpageRef.current);
        }
      }
    };
  }, [location.pathname, location.search]);

  // Track on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentPageRef.current) {
        const duration = Date.now() - startTimeRef.current;
        if (duration > 1000) {
          addPageVisit(currentPageRef.current, duration, currentSubpageRef.current);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
};

